import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Paper, Typography, Box, Button, TextField, MenuItem, Select, InputLabel, FormControl, Snackbar, Alert, CircularProgress } from '@mui/material';
import api from '../../api';
import { typesSeance, groupesMusculaires, exemplesAteliers, getSeanceTypeLabel, normalizeSeanceType } from './utils';

/**
 * ScanSeanceQR
 *
 * Amélioration : si ?seanceToken=... est présent, on récupère automatiquement l'eleve
 * via GET /muscu/token-info?seanceToken=... avant l'enregistrement. Ainsi on remplit
 * eleveId/eleveInfo et la redirection après save permettra à MonCarnet d'afficher les séances.
 */

export default function ScanSeanceQR() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const seanceToken = searchParams.get('seanceToken');
  const eleveParam = searchParams.get('eleve');

  const [eleveId, setEleveId] = useState(eleveParam || '');
  const [eleveInfo, setEleveInfo] = useState(null);
  const [loadingEleveInfo, setLoadingEleveInfo] = useState(false);

  const [date, setDate] = useState(new Date().toISOString().slice(0,10));
  const [type, setType] = useState(typesSeance[0]);
  const [atelier, setAtelier] = useState({ groupe: groupesMusculaires[0], atelierNom: exemplesAteliers[0], series: 3, repetitions: 10, poids: '', ressenti: 1 });
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // If seanceToken present, try to fetch eleve info automatically
    let mounted = true;
    async function fetchEleveByToken() {
      if (!seanceToken) return;
      setLoadingEleveInfo(true);
      try {
        // token-info supports query param seanceToken
        const res = await api.get(`/muscu/token-info?seanceToken=${encodeURIComponent(seanceToken)}`);
        if (!mounted) return;
        if (res.data && res.data.eleve) {
          const e = res.data.eleve;
          setEleveInfo(e);
          setEleveId(e._id || e.id || eleveParam || '');
        } else {
          setEleveInfo(null);
        }
      } catch (err) {
        console.warn('ScanSeanceQR: token-info failed', err?.response?.status || err.message);
        setEleveInfo(null);
      } finally {
        if (mounted) setLoadingEleveInfo(false);
      }
    }

    // If eleveParam provided, try to fetch its public info too (to display name)
    async function fetchEleveById(id) {
      if (!id) return;
      try {
        const res = await api.get(`/eleves/${id}`);
        if (!mounted) return;
        if (res.data) {
          setEleveInfo(res.data);
        }
      } catch (err) {
        // ignore, eleveInfo stays null
      }
    }

    if (seanceToken) fetchEleveByToken();
    else if (eleveParam) fetchEleveById(eleveParam);

    return () => { mounted = false; };
  }, [seanceToken, eleveParam]);

  const handleSave = async () => {
    if (!eleveId && !seanceToken) {
      setSnack({ open: true, message: 'Élève non identifié : QR invalide.', severity: 'error' });
      return;
    }
    if (!atelier.atelierNom) {
      setSnack({ open: true, message: 'Sélectionne un atelier.', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      // if we have only token but no eleveId (rare), try to fetch eleveInfo before saving
      if (!eleveId && seanceToken) {
        try {
          const res = await api.get(`/muscu/token-info?seanceToken=${encodeURIComponent(seanceToken)}`);
          if (res.data && res.data.eleve && (res.data.eleve._id || res.data.eleve.id)) {
            setEleveInfo(res.data.eleve);
            setEleveId(res.data.eleve._id || res.data.eleve.id);
          }
        } catch (err) {
          // continue: backend may accept token for creating seance even without returning eleve id
        }
      }

      const payload = {
        eleve: eleveId || undefined,
        date,
        type: normalizeSeanceType(type),
        ateliers: [atelier]
      };

      const config = {};
      if (seanceToken) config.headers = { 'x-seance-token': seanceToken };

      await api.post('/muscu/seances', payload, config);

      setSnack({ open: true, message: 'Séance enregistrée', severity: 'success' });

      // Build redirect query: include seanceToken (if present) so MonCarnet can resolve the eleve
      const params = new URLSearchParams();
      if (eleveId) params.set('eleve', eleveId);
      if (seanceToken) params.set('seanceToken', seanceToken);
      const q = params.toString();

      setTimeout(() => navigate('/carnet-muscu/mon-carnet' + (q ? `?${q}` : '')), 700);
    } catch (err) {
      console.error('ScanSeanceQR.save error', err);
      setSnack({ open: true, message: err.response?.data?.error || 'Erreur lors de l\'enregistrement', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 3, mt: 4 }}>
      <Typography variant="h6" gutterBottom>Enregistrement rapide depuis QR</Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        { seanceToken ? 'Token QR détecté — enregistrement sécurisé.' : 'Élève ID fourni via URL.' }
      </Typography>

      {loadingEleveInfo ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress /></Box>
      ) : eleveInfo ? (
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{eleveInfo.prenom} {eleveInfo.nom}</Typography>
          <Typography variant="body2" color="text.secondary">ID: {eleveInfo._id || eleveInfo.id}</Typography>
        </Box>
      ) : (
        !seanceToken && (
          <TextField label="Élève ID" value={eleveId} onChange={e => setEleveId(e.target.value)} fullWidth sx={{ mb: 2 }} />
        )
      )}

      <Box sx={{ display: 'grid', gap: 2 }}>
        <TextField
          label="Date"
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 220 }}
        />

        <FormControl sx={{ width: 220 }}>
          <InputLabel id="type-seance-label">Type de séance</InputLabel>
          <Select labelId="type-seance-label" value={type} label="Type de séance" onChange={e => setType(normalizeSeanceType(e.target.value))}>
            {typesSeance.map(t => <MenuItem key={t} value={t}>{getSeanceTypeLabel(t)}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel id="groupe-label">Groupe</InputLabel>
          <Select labelId="groupe-label" value={atelier.groupe} label="Groupe" onChange={e => setAtelier({...atelier, groupe: e.target.value})}>
            {groupesMusculaires.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel id="atelier-label">Atelier</InputLabel>
          <Select labelId="atelier-label" value={atelier.atelierNom} label="Atelier" onChange={e => setAtelier({...atelier, atelierNom: e.target.value})}>
            {exemplesAteliers.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </Select>
        </FormControl>

        <TextField label="Séries" type="number" value={atelier.series} onChange={e => setAtelier({...atelier, series: Number(e.target.value)})} />
        <TextField label="Répétitions" type="number" value={atelier.repetitions} onChange={e => setAtelier({...atelier, repetitions: Number(e.target.value)})} />
        <TextField label="Poids / distance" value={atelier.poids} onChange={e => setAtelier({...atelier, poids: e.target.value})} />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={() => { setAtelier({ groupe: groupesMusculaires[0], atelierNom: exemplesAteliers[0], series: 3, repetitions: 10, poids: '', ressenti: 1 }); }}>
            Réinitialiser
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement...' : 'Enregistrer la séance'}
          </Button>
        </Box>
      </Box>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({...snack, open:false})}>
        <Alert severity={snack.severity} onClose={() => setSnack({...snack, open:false})}>{snack.message}</Alert>
      </Snackbar>
    </Paper>
  );
}